import React, {
  useEffect, useRef, useState, Fragment,
} from 'react';
import { Menu as MenuIcon } from 'react-feather';
import { useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CallIcon from '@mui/icons-material/Call';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  Link,
  List,
  ListItem,
  ListSubheader,
  SvgIcon,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Logo from 'src/components/Logo';
import CopyText from 'src/components/CopyText/index';
import CopyTextMenu from 'src/components/CopyTextMenu';
import { buildReferralSignupLink } from 'src/utils/referralLink';
import DailyTextUsage from 'src/layouts/DashboardLayout/NavBar/DailyTextUsage';
import helpers from 'src/helpers';
import NavItemsParent from 'src/layouts/DashboardLayout/NavBar/NavItemsParent';
import NavItem from 'src/layouts/DashboardLayout/NavBar/NavItem';
import navConfig from 'src/layouts/DashboardLayout/NavBar/options';
import {
  refreshSetupAssistantOutstanding,
  SETUP_ASSISTANT_OUTSTANDING_ROUTE_THROTTLE_MS,
} from 'src/actions/setupAssistantActions';

import navBarStyle from 'src/layouts/DashboardLayout/NavBar/navBar.style';

import {
  PERMISSIONS,
} from 'src/constants';

const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;
const useStyles = makeStyles()(navBarStyle);

function NavBar({
  isDesktopNavOpen,
  logout,
  onMobileClose,
  openMobile,
  setDesktopNavOpen,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    user,
    pbAPIKeyValid,
    rateLimits,
  } = useSelector((state) => state.account);
  const [isButtonShowSmallDesktopClick, setButtonShowSmallDesktopClick] = useState(true);
  const { classes, cx } = useStyles();
  const {
    betaUser,
    userSubscription,
    userRole,
    userPhoneNumbersInfo,
    textingProvider,
  } = user || {};
  const {
    dailyTextHardLimit,
    dailyTextsSent,
  } = rateLimits || {};
  const {
    referralCode,
  } = userSubscription || {};
  const {
    permissions: userRolePermissions,
  } = userRole || {};
  const userCPNumber = userPhoneNumbersInfo?.phoneNumber;
  const referralSignupLink = buildReferralSignupLink(referralCode);
  const showYourCPNumber = (helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.YOUR_CP_PHONE_NUMBER_SIDEBAR, userRolePermissions) && userCPNumber);
  const showDailyTextUsage = !!(helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.DAILY_TEXT_USAGE_SIDEBAR, userRolePermissions) && userCPNumber && textingProvider === 'CP');
  // CON-1699 — the Setup Assistant entry. Same textingProvider test the
  // server's loader applies (SetupSnapshotService isCPTexting), so the nav
  // and the page's `applicable` flag agree by construction.
  const isCPTexting = textingProvider === 'CP';
  // The Setup Assistant badge's count is fetched only when the entry itself
  // is shown: the same two gates options.js applies to it.
  const showsSetupAssistant = isCPTexting && helpers.permissions.doesUserHavePermission(
    PERMISSIONS_SYSTEM_CODE.SETUP_ASSISTANT,
    userRolePermissions,
  );
  const isFirstSetupAssistantPath = useRef(true);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  // Once on mount (and again if the gates come to pass later, e.g. after
  // the account settings load).
  useEffect(() => {
    if (showsSetupAssistant) {
      dispatch(refreshSetupAssistantOutstanding());
    }
  }, [dispatch, showsSetupAssistant]);

  // On route change, at most once a minute. The mount call above covers
  // the first path, so it is skipped here.
  useEffect(() => {
    if (isFirstSetupAssistantPath.current) {
      isFirstSetupAssistantPath.current = false;
      return;
    }

    if (showsSetupAssistant) {
      dispatch(refreshSetupAssistantOutstanding({
        throttleMs: SETUP_ASSISTANT_OUTSTANDING_ROUTE_THROTTLE_MS,
      }));
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const isStuckOnSettings = helpers.isPageBlocked(user)
    || helpers.needOtherInfoNow(user)
    || helpers.needCustomFieldsNow(user, pbAPIKeyValid);
  const doesUserHaveSubscription = helpers.doesUserHaveSubscription(user);
  const isEmptyApiKey = helpers.isEmptyApiKey(user);

  const handleClickMenuOpen = () => {
    setDesktopNavOpen(isButtonShowSmallDesktopClick);
    setButtonShowSmallDesktopClick(!isButtonShowSmallDesktopClick);
  };

  const handleDrawerDesktopMouseEnter = () => {
    if (isButtonShowSmallDesktopClick) {
      setDesktopNavOpen(true);
    }
  };

  const handleDrawerDesktopMouseLeave = () => {
    if (isButtonShowSmallDesktopClick) {
      setDesktopNavOpen(false);
    }
  };

  const styleGreenIcon = {
    color: '#78DC78',
  };

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box className="navBox" p={2}>
          <Box
            className="navBoxUserName"
            mt={2}
            textAlign="center"
          >
            <Hidden lgDown>
              <IconButton color="inherit" onClick={handleClickMenuOpen} size="large">
                <SvgIcon fontSize="small">
                  {isButtonShowSmallDesktopClick ? <MenuIcon /> : <MenuOpenIcon />}
                </SvgIcon>
              </IconButton>
            </Hidden>
            <div className="navBoxUserNameIn">
              <Link
                component={RouterLink}
                to="/app/account"
                variant="h5"
                color="textPrimary"
                underline="none"
              >
                {`${user.firstName} ${user.lastName}`}
              </Link>
              <Typography variant="body2" color="textSecondary">
                {user.bio}
              </Typography>
            </div>
          </Box>
        </Box>
        <Divider />
        <Box className="navBox" p={2}>
          {navConfig(logout).filter((config) => (!config.betaOnly || betaUser) && (!config.cpTextingOnly || isCPTexting)).map((config, idx) => {
            const {
              subheader,
              isLink,
              href,
              icon,
              click,
              isShownWhenOtherHidden,
              betaOnly,
              items,
              permissions,
              requiredPermissions,
              additionalOptions,
            } = config;

            if (permissions && !helpers.permissions.doesUserHaveOneOfPermissions(permissions, userRolePermissions)) {
              return null;
            }

            // CON-1487 Phase G — AND semantics for admin sections.
            // `requiredPermissions` (all-must-hold) coexists with
            // `permissions` (any-may-hold); both must pass if both set.
            if (Array.isArray(requiredPermissions) && requiredPermissions.length) {
              const hasAll = requiredPermissions.every((p) => (
                helpers.permissions.doesUserHavePermission(p, userRolePermissions)
              ));
              if (!hasAll) return null;
            }

            return (
              <Fragment key={subheader}>
                {idx > 0 && (
                  <Divider
                    sx={{
                      mx: 1,
                      my: 0.25,
                      height: 1,
                      backgroundColor: (theme) => theme.palette.divider,
                    }}
                  />
                )}
                <List
                  key={subheader}
                  subheader={(
                    <ListSubheader
                      className="navSubHead"
                      disableGutters
                      disableSticky
                    >
                      {isLink && (!betaOnly || betaUser) ? (
                        <ul className={classes.navSubHeadFirstItem}>
                          <NavItem
                            key={subheader}
                            depth={0}
                            icon={icon}
                            title={subheader}
                            isPageBlocked={isStuckOnSettings || isEmptyApiKey}
                            doesUserHaveSubscription={doesUserHaveSubscription}
                            href={href}
                            click={click}
                            className="firstLine"
                            isShownWhenOtherHidden={isShownWhenOtherHidden}
                            additionalOptions={additionalOptions}
                          />
                        </ul>
                      ) : subheader}
                    </ListSubheader>
                  )}
                  className={isLink ? 'firstLineLink' : ''}
                >
                  <NavItemsParent
                    items={items}
                    pathname={location.pathname}
                    isPageBlocked={isStuckOnSettings}
                    doesUserHavePermission={helpers.permissions.doesUserHavePermission}
                    doesUserHaveSubscription={doesUserHaveSubscription}
                    isBetaUser={betaUser}
                    userRolePermissions={userRolePermissions}
                  />
                </List>
              </Fragment>
            );
          })}
          {showDailyTextUsage && (
            <DailyTextUsage
              dailyLimitCount={dailyTextHardLimit}
              textsUsedCount={dailyTextsSent}
            />
          )}
        </Box>
        <Divider />
        {showYourCPNumber && (
          <Box
            className="navBox"
            p={2}
            pb={0}
          >
            <List
              key="Your CP Number"
              subheader={(
                <ListSubheader
                  className="navSubHead"
                  disableGutters
                  disableSticky
                >
                  Your CP Number
                </ListSubheader>
              )}
            >
              <List disablePadding>
                <ListItem
                  className={cx(classes.itemLeaf, 'navItemNewLiForIcon')}
                  key={referralCode}
                  disableGutters
                >
                  <CallIcon
                    className={`${classes.icon} navItemIcon`}
                    size="20"
                    style={styleGreenIcon}
                  />
                  <span className={`${classes.title} navItemText`}>
                    <CopyText
                      text={helpers.transform.toPhoneNumber(userCPNumber)}
                      tooltipCopyText="Click to Copy"
                      tooltipCopiedText="Copied!"
                    />
                  </span>
                </ListItem>
              </List>
            </List>
          </Box>
        )}
        {
          helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.YOUR_REFERRAL_CODE_SIDEBAR, userRolePermissions) && (
            <Box
              className="navBox"
              p={2}
              pt={(showYourCPNumber && isDesktopNavOpen) ? 0 : 2}
            >
              <List
                key="Your Referral Code"
                subheader={(
                  <ListSubheader
                    className="navSubHead"
                    disableGutters
                    disableSticky
                  >
                    Your Referral Code
                  </ListSubheader>
                )}
              >
                <List disablePadding>
                  <ListItem
                    className={cx(classes.itemLeaf, 'navItemNewLiForIcon')}
                    key={referralCode}
                    disableGutters
                  >
                    <AttachMoneyIcon
                      className={`${classes.icon} navItemIcon`}
                      size="20"
                      style={styleGreenIcon}
                    />
                    <span className={`${classes.title} navItemText`}>
                      <CopyTextMenu
                        text={referralCode}
                        options={[
                          { label: 'Copy Referral Link', value: referralSignupLink },
                          { label: 'Copy Referral Code', value: referralCode },
                        ]}
                        tooltipCopyText="Click to Copy, share & earn a $2 CP Credit!"
                        tooltipCopiedText="Copied. Now Share!"
                      />
                    </span>
                  </ListItem>
                </List>
              </List>
            </Box>
          )
        }
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          classes={{
            paper: cx({
              [classes.desktopDrawer]: true,
              [classes.desktopSmallDrawer]: !isDesktopNavOpen,
            })
          }}
          open
          variant="persistent"
          {...isButtonShowSmallDesktopClick ? {
            onMouseEnter: handleDrawerDesktopMouseEnter,
            onMouseLeave: handleDrawerDesktopMouseLeave,
          } : {}}
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  isDesktopNavOpen: PropTypes.bool.isRequired,
  setDesktopNavOpen: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
